import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { subscriptionService } from '../../../services/subscriptionService';

const CurrentSubscription = ({ subscription, onManageBilling, onRefresh }) => {
  if (!subscription) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
          <Icon name="Package" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No Active Subscription</h3>
        <p className="text-muted-foreground mb-6">
          Choose a plan to get started with your organization's mental health support platform
        </p>
        <Button iconName="ArrowRight" iconPosition="right">
          View Plans
        </Button>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'trialing':
        return 'text-blue-600 bg-blue-100';
      case 'past_due':
        return 'text-yellow-600 bg-yellow-100';
      case 'canceled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const seatsPercentage = subscriptionService?.calculateUsagePercentage(
    subscription?.seatsUsed || 0,
    subscription?.plan?.seatLimit || 0
  );

  const sessionsPercentage = subscriptionService?.calculateUsagePercentage(
    subscription?.sessionsUsed || 0,
    subscription?.plan?.sessionLimit || 0
  );

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-primary/5 border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="Crown" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-bold">{subscription?.plan?.name} Plan</h2>
              <p className="text-sm text-muted-foreground capitalize">
                {subscription?.billingCycle} billing
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(subscription?.status)}`}>
              {subscription?.status}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Billing Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Current Period</p>
            <p className="font-medium">
              {formatDate(subscription?.currentPeriodStart)} - {formatDate(subscription?.currentPeriodEnd)}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Next Billing Date</p>
            <p className="font-medium">{formatDate(subscription?.currentPeriodEnd)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Amount</p>
            <p className="font-medium text-lg">
              {subscriptionService?.formatAmount(
                subscription?.billingCycle === 'yearly' 
                  ? subscription?.plan?.priceYearly 
                  : subscription?.plan?.priceMonthly,
                subscription?.plan?.currency
              )}
              <span className="text-sm text-muted-foreground">/{subscription?.billingCycle === 'yearly' ? 'year' : 'month'}</span>
            </p>
          </div>
        </div>

        {/* Usage Stats */}
        <div className="space-y-4">
          <h3 className="font-semibold">Usage Statistics</h3>
          
          {/* Seats Usage */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Active Users</span>
              <span className="font-medium">
                {subscription?.seatsUsed || 0}
                {subscription?.plan?.seatLimit !== -1 && ` / ${subscription?.plan?.seatLimit}`}
                {subscription?.plan?.seatLimit === -1 && ' / Unlimited'}
              </span>
            </div>
            {subscription?.plan?.seatLimit !== -1 && (
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div 
                  className={`h-full transition-all ${
                    seatsPercentage >= 90 ? 'bg-red-500' : 
                    seatsPercentage >= 70 ? 'bg-yellow-500': 'bg-green-500'
                  }`}
                  style={{ width: `${seatsPercentage}%` }}
                />
              </div>
            )}
          </div>

          {/* Sessions Usage */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Sessions This Period</span>
              <span className="font-medium">
                {subscription?.sessionsUsed || 0}
                {subscription?.plan?.sessionLimit !== -1 && ` / ${subscription?.plan?.sessionLimit}`}
                {subscription?.plan?.sessionLimit === -1 && ' / Unlimited'}
              </span>
            </div>
            {subscription?.plan?.sessionLimit !== -1 && (
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div 
                  className={`h-full transition-all ${
                    sessionsPercentage >= 90 ? 'bg-red-500' : 
                    sessionsPercentage >= 70 ? 'bg-yellow-500': 'bg-green-500'
                  }`}
                  style={{ width: `${sessionsPercentage}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="space-y-2">
          <h3 className="font-semibold">Plan Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {subscription?.plan?.features?.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <Icon name="Check" size={16} className="text-green-600" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-4 border-t border-border">
          <Button 
            onClick={onManageBilling}
            iconName="Settings"
            iconPosition="left"
            variant="outline"
            disabled={!subscription}
            title={!subscription ? 'Subscribe to a plan first to manage billing' : 'Manage your subscription and billing'}
          >
            Manage Subscription
          </Button>
          <Button 
            onClick={onRefresh}
            iconName="RefreshCw"
            iconPosition="left"
            variant="ghost"
          >
            Refresh
          </Button>
        </div>

        {/* Cancellation Notice */}
        {subscription?.cancelAtPeriodEnd && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-900">Subscription Ending</p>
              <p className="text-sm text-yellow-700">
                Your subscription will end on {formatDate(subscription?.currentPeriodEnd)}. 
                You can reactivate it anytime before this date.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentSubscription;